import type {
  MobileAppId, MobilePlatform, MobileCheckInRequest, MobileCheckInResponse, MobilePermissions,
} from '@platform/shared-types';
import { KEYS, readJson, safeSet, safeRemove, getOrCreateDeviceId } from './storage';
import type { KeyValueStore } from './storage';

export interface ShellConfig {
  app: MobileAppId;
  platform: MobilePlatform;
  env: 'dev' | 'prod';
  /** Absolute URL of POST /mobile/check-in (P1). */
  checkInUrl: string;
  timeoutMs?: number;
}

/** P3 adapter: CapacitorHttp (native HTTP, no WebView CORS). `data` is parsed JSON. Throws on network error/timeout. */
export interface HttpPort {
  postJson(url: string, body: unknown, timeoutMs: number, headers?: Record<string, string>): Promise<{ status: number; data: unknown }>;
}

export interface DeviceInfoPort {
  nativeVersion(): Promise<string>;
  /** iOS CFBundleVersion / Android versionCode — compared as an integer server-side. */
  nativeBuild(): Promise<number>;
  osVersion(): Promise<string | undefined>;
  model(): Promise<string | undefined>;
}

export interface CheckInPorts {
  store: KeyValueStore;
  http: HttpPort;
  device: DeviceInfoPort;
  currentBundleId(): Promise<string | null>;
  permissions(): Promise<MobilePermissions>;
  uuid(): string;
  now(): number;
}

export type CheckInSource = 'live' | 'cache' | 'default';
export interface CheckInResult { response: MobileCheckInResponse; source: CheckInSource }
export type LastApply = NonNullable<MobileCheckInRequest['lastApply']>;

export const DEFAULT_TIMEOUT_MS = 4000;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export function isLastApply(v: unknown): v is LastApply {
  return isObj(v) && typeof v.bundleId === 'string' && v.bundleId.length > 0 && v.bundleId.length <= 64
    && (v.outcome === 'ok' || v.outcome === 'reverted');
}

/** Own guard (no runtime shared-types import — see Global Constraints). */
export function isCheckInResponse(v: unknown): v is MobileCheckInResponse {
  if (!isObj(v)) return false;
  if (v.gate !== 'ok' && v.gate !== 'recommended' && v.gate !== 'required') return false;
  if (v.storeUrl !== null && typeof v.storeUrl !== 'string') return false;
  if (typeof v.resetToBuiltin !== 'boolean') return false;
  if (v.applyMode !== 'instant' && v.applyMode !== 'next-open') return false;
  if (typeof v.checkedAt !== 'number') return false;
  if (v.bundle === null) return true;
  const b = v.bundle;
  return isObj(b) && typeof b.bundleId === 'string' && typeof b.url === 'string'
    && typeof b.sha256 === 'string' && /^[a-f0-9]{64}$/.test(b.sha256);
}

export function allowAll(now: number): MobileCheckInResponse {
  return { gate: 'ok', storeUrl: null, bundle: null, resetToBuiltin: false, applyMode: 'next-open', checkedAt: now };
}

/** Cached last live answer, with the native build it was given to. */
interface CachedCheckIn { response: MobileCheckInResponse; nativeBuild: number | null }
const isCached = (v: unknown): v is CachedCheckIn =>
  isObj(v) && isCheckInResponse(v.response) && (v.nativeBuild === null || typeof v.nativeBuild === 'number');

const clip = (s: string | undefined, n: number) => (s === undefined ? undefined : s.slice(0, n));

/**
 * Ask the server what this phone may run (spec §4.2). Never throws, never
 * blocks on our own outage: live → cache (gate kept, stale bundle offer
 * dropped) → allow.
 */
export async function runCheckIn(ports: CheckInPorts, config: ShellConfig): Promise<CheckInResult> {
  const { store } = ports;
  const lastApply = await readJson(store, KEYS.lastApply, isLastApply);
  let permissions: MobilePermissions = {};
  try { permissions = await ports.permissions(); } catch { /* report nothing */ }

  let nativeBuild: number | null = null;
  try {
    nativeBuild = await ports.device.nativeBuild();
    // Clipped to the server's limits: a 400 must never be self-inflicted.
    const base: MobileCheckInRequest = {
      app: config.app, platform: config.platform, env: config.env,
      deviceId: await getOrCreateDeviceId(store, ports.uuid),
      nativeVersion: clip(await ports.device.nativeVersion(), 32) as string,
      nativeBuild,
      bundleId: await ports.currentBundleId(),
      permissions,
      osVersion: clip(await ports.device.osVersion(), 32),
      model: clip(await ports.device.model(), 64),
    };
    const timeout = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    let res = await ports.http.postJson(config.checkInUrl, lastApply ? { ...base, lastApply } : base, timeout);
    if (lastApply && res.status >= 400 && res.status < 500) {
      // A report the server rejects must never wedge every future check-in.
      await safeRemove(store, KEYS.lastApply);
      res = await ports.http.postJson(config.checkInUrl, base, timeout);
    }
    if (res.status === 200 && isCheckInResponse(res.data)) {
      await safeSet(store, KEYS.lastCheckIn, JSON.stringify({ response: res.data, nativeBuild }));
      if (lastApply) await safeRemove(store, KEYS.lastApply);
      return { response: res.data, source: 'live' };
    }
  } catch {
    /* offline, timeout, or a native probe failed — fall through */
  }

  const cached = await readJson(store, KEYS.lastCheckIn, isCached);
  if (cached) {
    // Updated from the store since that answer? Its gate no longer applies.
    const updatedSince = cached.nativeBuild !== null && nativeBuild !== null && nativeBuild > cached.nativeBuild;
    const gate = updatedSince ? 'ok' : cached.response.gate;
    return { response: { ...cached.response, gate, bundle: null, resetToBuiltin: false }, source: 'cache' };
  }
  return { response: allowAll(ports.now()), source: 'default' };
}
